import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { getAuthHeaders } from "../utils/getAuthHeaders";
import { getErrorMessage } from "../utils/getErrorMessage";
import { endpoints } from "../config/api";
import { fetchAllOrgUsers } from "../utils/fetchOrgUsers";
import { normalizeUserTypeValue, userTypeForApi, getUserTypeMeta } from "../constants/userTypes";
import { UserTypeSelect } from "./UserTypeControls";

const shimmerCellStyle = {
  height: "20px",
  borderRadius: "4px",
  background: "linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%)",
  backgroundSize: "200% 100%",
  animation: "usersListShimmer 1.5s ease-in-out infinite",
};

const ShimmerRow = () => (
  <tr>
    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
      <td key={i} style={{ padding: "12px 14px", border: "1px solid #e5e7eb" }}>
        <div style={shimmerCellStyle} />
      </td>
    ))}
  </tr>
);

const UsersList = ({ onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [actionError, setActionError] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [moveOrgAction, setMoveOrgAction] = useState(null);
  const [moveConfirmText, setMoveConfirmText] = useState("");
  const [targetOrgId, setTargetOrgId] = useState("");

  useEffect(() => {
    const fetchOrgUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const nextData = await fetchAllOrgUsers(endpoints.orgsUsers, { limit: 100 });
        setData(nextData);
      } catch (err) {
        console.error("Error fetching org users", err);
        setError(getErrorMessage(err, "Failed to load users"));
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrgUsers();
  }, []);

  const allRows = useMemo(
    () =>
      data.flatMap((org) =>
        (org.users || []).map((user) => ({
          organization_id: org.organization_id,
          org_name: org.name || "—",
          org_icon: org.icon,
          org_created_at: org.created_at,
          ...user,
          isActive:
            typeof user?.isActive === "boolean"
              ? user.isActive
              : String(user?.isActive).toLowerCase() === "false"
              ? false
              : true,
        }))
      ),
    [data]
  );

  const orgOptions = useMemo(
    () => data.map((org) => ({ id: org.organization_id, name: org.name || "—" })),
    [data]
  );

  const rows = useMemo(() => {
    if (!search.trim()) return allRows;
    const q = search.trim().toLowerCase();
    return allRows.filter((row) => {
      const typeLabel = getUserTypeMeta(row.user_type).label.toLowerCase();
      return (
        (row.org_name && String(row.org_name).toLowerCase().includes(q)) ||
        (row.name && String(row.name).toLowerCase().includes(q)) ||
        (row.email && String(row.email).toLowerCase().includes(q)) ||
        typeLabel.includes(q) ||
        (row.user_type && String(row.user_type).toLowerCase().includes(q))
      );
    });
  }, [allRows, search]);

  const updateUserStatusInState = (userId, isActive) => {
    setData((prev) =>
      prev.map((org) => ({
        ...org,
        users: (org.users || []).map((user) =>
          user.user_id === userId ? { ...user, isActive } : user
        ),
      }))
    );
  };

  const updateUserTypeInState = (userId, userType) => {
    setData((prev) =>
      prev.map((org) => ({
        ...org,
        users: (org.users || []).map((user) =>
          user.user_id === userId ? { ...user, user_type: userType || null } : user
        ),
      }))
    );
  };

  const updateUserOrgInState = (userId, targetOrganizationId) => {
    setData((prev) => {
      let movedUser = null;
      const withoutUser = prev.map((org) => {
        const found = (org.users || []).find((u) => u.user_id === userId);
        if (found) movedUser = found;
        return { ...org, users: (org.users || []).filter((u) => u.user_id !== userId) };
      });
      if (!movedUser) return prev;
      return withoutUser.map((org) =>
        org.organization_id === targetOrganizationId
          ? { ...org, users: [...(org.users || []), movedUser] }
          : org
      );
    });
  };

  const handleUserTypeChange = async (row, nextUserType) => {
    const userId = row.user_id;
    if (!userId) return;

    const previous = row.user_type || null;
    const normalizedPrevious = normalizeUserTypeValue(previous);
    if (normalizedPrevious === nextUserType) return;

    setActionError(null);
    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    const apiValue = userTypeForApi(nextUserType);
    updateUserTypeInState(userId, apiValue);

    try {
      const response = await axios.patch(
        endpoints.authUserType,
        {
          userId,
          user_type: apiValue,
        },
        {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const updatedUserId = response?.data?.data?.user_id || userId;
      const updatedType =
        response?.data?.data?.user_type !== undefined
          ? response.data.data.user_type
          : apiValue;
      updateUserTypeInState(updatedUserId, updatedType);
    } catch (err) {
      console.error("Error updating user type", err);
      updateUserTypeInState(userId, previous);
      setActionError(getErrorMessage(err, "Failed to update user type"));
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const handleToggleAccess = async (row) => {
    const userId = row.user_id;
    if (!userId) return;

    const nextIsActive = !(row.isActive ?? true);
    setActionError(null);
    setActionLoading((prev) => ({ ...prev, [userId]: true }));

    try {
      const response = await axios.post(
        endpoints.authStatus,
        { userId, isActive: nextIsActive },
        {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const updatedUserId = response?.data?.data?.user_id || userId;
      const updatedIsActive =
        typeof response?.data?.data?.isActive === "boolean"
          ? response.data.data.isActive
          : nextIsActive;
      updateUserStatusInState(updatedUserId, updatedIsActive);
    } catch (err) {
      console.error("Error updating user status", err);
      setActionError(getErrorMessage(err, "Failed to update user access"));
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const openConfirmModal = (row) => {
    setConfirmText("");
    setConfirmAction({
      userId: row.user_id,
      name: row.name || row.email || "this user",
      nextIsActive: !(row.isActive ?? true),
    });
  };

  const closeConfirmModal = () => {
    const loadingUserId = confirmAction?.userId;
    if (loadingUserId && actionLoading[loadingUserId]) return;
    setConfirmAction(null);
    setConfirmText("");
  };

  const openMoveOrgModal = (row) => {
    setMoveConfirmText("");
    setTargetOrgId("");
    setMoveOrgAction({
      userId: row.user_id,
      userName: row.name || row.email || "this user",
      currentOrgId: row.organization_id,
    });
  };

  const closeMoveOrgModal = () => {
    const loadingUserId = moveOrgAction?.userId;
    if (loadingUserId && actionLoading[loadingUserId]) return;
    setMoveOrgAction(null);
    setMoveConfirmText("");
    setTargetOrgId("");
  };

  const handleMoveUserOrg = async () => {
    if (!moveOrgAction || !targetOrgId) return;
    const { userId } = moveOrgAction;
    setActionError(null);
    setActionLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      await axios.patch(
        endpoints.adminMoveUserOrg,
        { userId, organizationId: Number(targetOrgId) },
        {
          headers: {
            ...getAuthHeaders(),
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );
      updateUserOrgInState(userId, Number(targetOrgId));
      setMoveOrgAction(null);
      setMoveConfirmText("");
      setTargetOrgId("");
    } catch (err) {
      console.error("Error moving user to org", err);
      setActionError(getErrorMessage(err, "Failed to move user to organization"));
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const closeButtonStyle = {
    height: "40px",
    padding: "0 16px",
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#3730a3",
    color: "#ffffff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "600",
    marginBottom: "20px",
    alignSelf: "flex-start",
  };

  const titleStyle = {
    color: "#3730a3",
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "20px",
  };

  const searchInputStyle = {
    width: "100%",
    maxWidth: "400px",
    padding: "12px 14px 12px 40px",
    fontSize: "14px",
    border: "1px solid #3730a3",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#1f2937",
    outline: "none",
    boxSizing: "border-box",
  };

  const searchWrapperStyle = {
    position: "relative",
    marginBottom: "20px",
    width: "100%",
    maxWidth: "400px",
  };

  const searchIconStyle = {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
    fontSize: "18px",
  };

  const tableWrapperStyle = {
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "12px 14px",
    backgroundColor: "#3730a3",
    color: "#fff",
    fontWeight: "600",
    border: "1px solid #3730a3",
  };

  const tdStyle = {
    padding: "12px 14px",
    border: "1px solid #e5e7eb",
    color: "#1f2937",
  };

  const groupHeaderRowStyle = {
    backgroundColor: "#eef2ff",
  };

  const groupHeaderCellStyle = {
    padding: "10px 14px",
    border: "1px solid #c7d2fe",
    color: "#312e81",
    fontWeight: 700,
    letterSpacing: "0.2px",
  };

  const trEvenStyle = {
    backgroundColor: "#f9fafb",
  };

  const avatarStyle = {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    objectFit: "cover",
  };

  const actionButtonStyle = {
    height: "32px",
    padding: "0 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    background: "#fff",
    color: "#111827",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "12px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  };

  const modalOverlayStyle = {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(17, 24, 39, 0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "16px",
  };

  const modalContentStyle = {
    width: "100%",
    maxWidth: "480px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
    padding: "20px",
    boxSizing: "border-box",
  };

  const modalTitleStyle = {
    margin: 0,
    marginBottom: "10px",
    color: "#111827",
    fontWeight: "700",
    fontSize: "18px",
  };

  const modalBodyStyle = {
    margin: 0,
    marginBottom: "10px",
    color: "#374151",
    fontSize: "14px",
    lineHeight: "1.5",
  };

  const modalInputStyle = {
    width: "100%",
    height: "38px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    padding: "0 10px",
    boxSizing: "border-box",
    marginBottom: "14px",
  };

  const modalButtonsStyle = {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  };

  const modalCancelButtonStyle = {
    height: "36px",
    padding: "0 12px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  };

  const modalConfirmButtonStyle = {
    height: "36px",
    padding: "0 14px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#3730a3",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  };

  const modalConfirmButtonDisabledStyle = {
    ...modalConfirmButtonStyle,
    backgroundColor: "#9ca3af",
    cursor: "not-allowed",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <style>
        {`@keyframes usersListShimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }`}
      </style>
      {onClose && (
        <button style={closeButtonStyle} type="button" onClick={onClose}>
          Close
        </button>
      )}
      <h2 style={titleStyle}>Users List - Give or Revoke Access from Any User</h2>

      {!loading && !error && (
        <div style={searchWrapperStyle}>
          <span style={searchIconStyle} aria-hidden>⌕</span>
          <input
            type="search"
            placeholder="Search by organization, name, email, user type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={searchInputStyle}
            aria-label="Search users"
          />
        </div>
      )}

      {error && <p style={{ color: "#b91c1c", marginBottom: "12px" }}>{error}</p>}
      {!error && actionError && (
        <p style={{ color: "#b91c1c", marginBottom: "12px" }}>{actionError}</p>
      )}

      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Profile</th>
              <th style={thStyle}>Created At</th>
              <th style={thStyle}>Organization</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>User Type</th>
              <th style={thStyle}>Access</th>
              <th style={{ ...thStyle, whiteSpace: "nowrap" }}>Move Org</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <>
                {[...Array(8)].map((_, i) => (
                  <ShimmerRow key={i} />
                ))}
              </>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ ...tdStyle, textAlign: "center" }}>
                  {search.trim() ? "No users match your search." : "No users found."}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => {
                const previousRow = rows[idx - 1];
                const isNewOrgGroup =
                  idx === 0 || previousRow?.organization_id !== row.organization_id;
                const userTypeValue = normalizeUserTypeValue(row.user_type);

                return (
                  <React.Fragment key={`${row.organization_id}-${row.user_id}`}>
                    {isNewOrgGroup && (
                      <>
                        {idx !== 0 && (
                          <tr>
                            <td
                              colSpan={8}
                              style={{
                                border: "none",
                                padding: 0,
                                height: "10px",
                                background: "transparent",
                              }}
                            />
                          </tr>
                        )}
                        <tr style={groupHeaderRowStyle}>
                        <td colSpan={8} style={groupHeaderCellStyle}>
                          {row.org_name}
                        </td>
                        </tr>
                      </>
                    )}
                    <tr
                      style={{
                        ...(idx % 2 === 1 ? trEvenStyle : {}),
                        opacity: row.isActive === false ? 0.55 : 1,
                      }}
                    >
                      <td style={tdStyle}>
                        {row.profile_pic ? <img src={row.profile_pic} alt="" style={avatarStyle} /> : "—"}
                      </td>
                      <td style={tdStyle}>
                        {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
                      </td>
                      <td style={tdStyle}>{row.org_name}</td>
                      <td style={tdStyle}>{row.email ?? "—"}</td>
                      <td style={tdStyle}>{row.name ?? "—"}</td>
                      <td style={tdStyle}>
                        <UserTypeSelect
                          value={userTypeValue}
                          disabled={!!actionLoading[row.user_id]}
                          onChange={(next) => handleUserTypeChange(row, next)}
                          ariaLabel={`User type for ${row.email || row.name || row.user_id}`}
                        />
                      </td>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          style={actionButtonStyle}
                          disabled={!!actionLoading[row.user_id]}
                          onClick={() => openConfirmModal(row)}
                        >
                          {actionLoading[row.user_id]
                            ? "Updating..."
                            : row.isActive === false
                            ? "↺ Give Access"
                            : "⛔ Revoke"}
                        </button>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          style={{ ...actionButtonStyle, whiteSpace: "nowrap" }}
                          disabled={!!actionLoading[row.user_id]}
                          onClick={() => openMoveOrgModal(row)}
                        >
                          ⇄ Move Org
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {moveOrgAction && (
        <div style={modalOverlayStyle} onClick={closeMoveOrgModal}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={modalTitleStyle}>Move user to another organization</h3>
            <p style={modalBodyStyle}>
              Select the target organization for <strong>{moveOrgAction.userName}</strong>.
            </p>
            <select
              value={targetOrgId}
              onChange={(e) => setTargetOrgId(e.target.value)}
              style={{ ...modalInputStyle, cursor: "pointer" }}
              aria-label="Select target organization"
            >
              <option value="">— Select organization —</option>
              {orgOptions
                .filter((org) => org.id !== moveOrgAction.currentOrgId)
                .map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
            </select>
            <p style={{ ...modalBodyStyle, marginBottom: "8px" }}>
              Type <strong>move</strong> to confirm:
            </p>
            <input
              type="text"
              value={moveConfirmText}
              onChange={(e) => setMoveConfirmText(e.target.value)}
              placeholder="move"
              style={modalInputStyle}
              aria-label="Type confirmation keyword"
            />
            <div style={modalButtonsStyle}>
              <button
                type="button"
                style={modalCancelButtonStyle}
                onClick={closeMoveOrgModal}
                disabled={!!actionLoading[moveOrgAction.userId]}
              >
                Cancel
              </button>
              <button
                type="button"
                style={
                  targetOrgId && moveConfirmText.trim().toLowerCase() === "move"
                    ? modalConfirmButtonStyle
                    : modalConfirmButtonDisabledStyle
                }
                disabled={
                  !!actionLoading[moveOrgAction.userId] ||
                  !targetOrgId ||
                  moveConfirmText.trim().toLowerCase() !== "move"
                }
                onClick={handleMoveUserOrg}
              >
                {actionLoading[moveOrgAction.userId] ? "Moving..." : "Confirm Move"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <div style={modalOverlayStyle} onClick={closeConfirmModal}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={modalTitleStyle}>
              {confirmAction.nextIsActive ? "Give access back" : "Revoke access"}
            </h3>
            <p style={modalBodyStyle}>
              Are you sure you want to {confirmAction.nextIsActive ? "give access back to" : "revoke access from"}{" "}
              <strong>{confirmAction.name}</strong>?
            </p>
            <p style={{ ...modalBodyStyle, marginBottom: "8px" }}>
              Type{" "}
              <strong>{confirmAction.nextIsActive ? "give" : "revoke"}</strong>{" "}
              to confirm:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={confirmAction.nextIsActive ? "give" : "revoke"}
              style={modalInputStyle}
              aria-label="Type confirmation keyword"
            />
            <div style={modalButtonsStyle}>
              <button
                type="button"
                style={modalCancelButtonStyle}
                onClick={closeConfirmModal}
                disabled={!!actionLoading[confirmAction.userId]}
              >
                Cancel
              </button>
              <button
                type="button"
                style={
                  (confirmText || "").trim().toLowerCase() ===
                  (confirmAction.nextIsActive ? "give" : "revoke")
                    ? modalConfirmButtonStyle
                    : modalConfirmButtonDisabledStyle
                }
                disabled={
                  !!actionLoading[confirmAction.userId] ||
                  (confirmText || "").trim().toLowerCase() !==
                    (confirmAction.nextIsActive ? "give" : "revoke")
                }
                onClick={async () => {
                  const actionRow = rows.find((r) => r.user_id === confirmAction.userId);
                  if (!actionRow) return;
                  await handleToggleAccess(actionRow);
                  setConfirmAction(null);
                  setConfirmText("");
                }}
              >
                {actionLoading[confirmAction.userId] ? "Updating..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
